import { always, cond, equals } from 'ramda';
import React, { FC, useMemo } from 'react';

import { IBaseCard } from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import BackAndNextNav from '@ui/molecules/BackAndNextNav';
import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import ReadMore from '@ui/molecules/ReadMore';
import RoundedCard from '@ui/molecules/RoundedCard';

import aligned from '@ui/styled/Aligned';
import autoWidth from '@ui/styled/AutoWidth';
import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { interpolate } from '@utils/butter';

import { DeliveryTypes } from '@folx-types/delivery';
import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import { medicationDeliveryConstants } from '../MedicationDeliveryStep';
import { pharmacyDataConstants } from '../PharmacySelectionStep';

export interface IDeliveryConfirmationStepPageContent {
	pageHeader: string;
	pharmacyTopText: string;
	folxDescription: string;
	capsuleDescription: string;
	editLabel: string;
	expectHeader: string;
	pharmacyExpectation: string;
	folxExpectation: string;
	capsuleExpectation: string;
	testosteroneDisclaimer: string;
}

interface IDeliveryConfirmationStepProps extends IFlowStepComponentProps {
	pageContent: IDeliveryConfirmationStepPageContent;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const AlignedColumn = aligned<IFlexColumn>(FullWidthColumn);
const SpacedHeader = spaced(HeadingText);
const SpacedCard = spaced<IBaseCard>(RoundedCard);
const SpacedButton = autoWidth<IButtonProps>(spaced<IButtonProps>(Button));
const SpacedReadMore = spaced<IReadMoreProps>(ReadMore);

const DeliveryConfirmationStep: FC<IDeliveryConfirmationStepProps> = ({
	destinationFlow,
	existingFormData,
	handleNavNext,
	handleNavBack,
	pageContent,
}) => {
	const {
		pageHeader,
		pharmacyTopText,
		folxDescription,
		capsuleDescription,
		editLabel,
		expectHeader,
		folxExpectation,
		capsuleExpectation,
		pharmacyExpectation,
		testosteroneDisclaimer,
	} = pageContent;

	const deliveryMethod: DeliveryTypes = useMemo(
		() =>
			existingFormData?.[medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD] ??
			medicationDeliveryConstants.FOLX_METHOD,
		[existingFormData]
	);

	const isPharmacyMethod = useMemo(
		() => equals(deliveryMethod, medicationDeliveryConstants.PHARMACY_METHOD),
		[deliveryMethod]
	);

	const getPharmacyCard = () => {
		const pharmacyName = cond<DeliveryTypes, (v: DeliveryTypes) => boolean, string>([
			[equals(medicationDeliveryConstants.FOLX_METHOD), always('FOLX')],
			[equals(medicationDeliveryConstants.CAPSULE_METHOD), always('Capsule')],
			[
				equals(medicationDeliveryConstants.PHARMACY_METHOD),
				always(existingFormData?.[pharmacyDataConstants.DELIVERY_NAME]),
			],
		])(deliveryMethod);

		const pharmacyDescription = cond<DeliveryTypes, (v: DeliveryTypes) => boolean, string>([
			[equals(medicationDeliveryConstants.FOLX_METHOD), always(folxDescription)],
			[equals(medicationDeliveryConstants.CAPSULE_METHOD), always(capsuleDescription)],
			[
				equals(medicationDeliveryConstants.PHARMACY_METHOD),
				always(existingFormData?.[pharmacyDataConstants.DELIVERY_ADDRESS]),
			],
		])(deliveryMethod);

		return (
			<>
				<BasicText variant="field">{pharmacyName}</BasicText>
				<BasicText>{pharmacyDescription}</BasicText>
			</>
		);
	};

	const deliveryExpectation = useMemo(
		() =>
			cond<DeliveryTypes, (v: DeliveryTypes) => boolean, string>([
				[equals(medicationDeliveryConstants.FOLX_METHOD), always(folxExpectation)],
				[equals(medicationDeliveryConstants.CAPSULE_METHOD), always(capsuleExpectation)],
				[equals(medicationDeliveryConstants.PHARMACY_METHOD), always(pharmacyExpectation)],
			])(deliveryMethod),
		[deliveryMethod]
	);

	return (
		<CenteredGridItem>
			<AlignedColumn alignItems="stretch">
				<SpacedHeader marginBottom="sm">{pageHeader}</SpacedHeader>
				<SpacedCard
					bgColor="beige"
					marginBottom="lgxl"
					footer={
						isPharmacyMethod && (
							<SpacedButton
								marginTop="lg"
								marginRight="auto"
								marginLeft="none"
								labelSize="xs"
								href="../delivery/pharmacy_selection"
								label={editLabel}
							/>
						)
					}>
					<FullWidthColumn gap="xs">
						<BasicText variant="fineprintBold">{pharmacyTopText}</BasicText>
						{getPharmacyCard()}
					</FullWidthColumn>
				</SpacedCard>
				<SpacedReadMore label={expectHeader} marginBottom="xxl">
					<WYSIWYGContent
						dangerouslySetInnerHTML={{
							__html: equals(destinationFlow, 'hrt_t')
								? interpolate(deliveryExpectation, {
										additionalDisclaimer: testosteroneDisclaimer,
								  })
								: interpolate(deliveryExpectation, {
										additionalDisclaimer: '',
								  }),
						}}
					/>
				</SpacedReadMore>
				<BackAndNextNav backOnClick={handleNavBack} nextOnClick={handleNavNext} />
			</AlignedColumn>
		</CenteredGridItem>
	);
};

export default DeliveryConfirmationStep;
